import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSlidingStrip } from '@/shared/hooks/use-sliding-strip';
import { CheckIcon, ChevronDownIcon } from '@radix-ui/react-icons';
import { Text, UnstyledButton } from '@/shared/ui';
import { useIsMobile } from '@/shared/hooks/use-is-mobile';
import { cn } from '@/shared/ui/utils/tailwind';

export const TABS = ['Visao Geral', 'Caracteristicas', 'Visitas', 'Documentos', 'Propostas', 'Publicação'] as const;
export type TabName = (typeof TABS)[number];

const TAB_LABEL_KEYS: Record<TabName, string> = {
  'Visao Geral': 'detail.tabs.overview',
  Caracteristicas: 'detail.tabs.characteristics',
  Visitas: 'detail.tabs.visits',
  Documentos: 'detail.tabs.documents',
  Propostas: 'detail.tabs.proposals',
  'Publicação': 'detail.tabs.publications',
};

/** How many records each tab holds. Absent means the tab shows no count. */
export type TabCounts = Partial<Record<TabName, number>>;

interface TabBarProps {
  activeTab: TabName;
  onTabChange: (tab: TabName) => void;
  counts?: TabCounts;
}

/**
 * The small pill carrying a tab's record count.
 */
const CountPill: React.FC<{ count: number }> = ({ count }) => (
  <Text
    component="span"
    className="inline-flex h-[17px] items-center rounded-(--radius-full) bg-[var(--surface-tertiary)] px-[var(--space-1-5)] text-[length:var(--text-meta)] [font-weight:var(--font-semibold)] text-[var(--text-tertiary)]"
  >
    {count}
  </Text>
);

/**
 * Horizontal tablist for the property detail view, on viewports wide enough
 * to hold six tabs without cutting any of them off.
 */
const DesktopTabs: React.FC<TabBarProps> = ({ activeTab, onTabChange, counts }) => {
  const { t } = useTranslation('features/properties');
  const activeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    activeRef.current?.scrollIntoView({ inline: 'center', block: 'nearest' });
  }, [activeTab]);

  return (
    <div
      role="tablist"
      className="flex flex-nowrap overflow-x-auto border-b border-[var(--border-subtle)] scrollbar-none"
    >
      {TABS.map((tab) => {
        const selected = activeTab === tab;
        const count = counts?.[tab];
        return (
          <UnstyledButton
            key={tab}
            ref={selected ? activeRef : undefined}
            role="tab"
            aria-selected={selected}
            onClick={() => onTabChange(tab)}
            className={cn(
              'inline-flex shrink-0 items-center gap-[var(--space-1-5)] whitespace-nowrap border-b-2 border-solid px-[var(--space-4)] py-[var(--space-3)] text-[length:var(--text-body-sm)]',
              selected
                ? '[font-weight:var(--font-semibold)] text-(--seed-base) border-b-(--seed-base)'
                : '[font-weight:var(--font-medium)] text-[var(--text-secondary)] border-b-transparent hover:text-[var(--text-primary)]',
            )}
          >
            {t(TAB_LABEL_KEYS[tab])}
            {count != null && <CountPill count={count} />}
          </UnstyledButton>
        );
      })}
    </div>
  );
};

/**
 * The same six tabs as a selector, for phone widths.
 *
 * Six tabs do not fit across a phone, and a horizontally scrolling tablist
 * hides the last ones behind an edge nobody thinks to drag: Publicação would
 * become unreachable in practice. The selector shows all six at once, with
 * their counts, and costs one tap.
 */
/**
 * The tabs on a phone: a horizontal strip that stays put while you scroll.
 *
 * It used to be a dropdown. A dropdown hides five of the six destinations
 * behind a press, which makes "is there anything in Documentos" a question
 * you have to open a menu to answer — and the counts, which exist precisely
 * to answer that without opening anything, were invisible. As a strip the
 * counts are readable at a glance and the current tab is visible in the
 * same look the desktop uses.
 *
 * Sticky, so the tabs stay reachable through a long tab body: on a 702px
 * screen the Documentos list runs well past the fold, and a tab bar you have
 * to scroll back up to is a tab bar you stop using.
 */
const MobileTabSelector: React.FC<TabBarProps> = ({ activeTab, onTabChange, counts }) => {
  const { t } = useTranslation('features/properties');
  const strip = useSlidingStrip();

  return (
    <div
      className="sticky top-0 z-10 -mx-(--space-5) px-(--space-5) py-(--space-1)"
      style={{ background: 'var(--surface-primary)' }}
    >
      <div
        ref={strip.ref}
        {...strip.props}
        role="tablist"
        data-testid="property-tab-selector"
        className={`flex gap-(--space-2) ${strip.props.className}`}
      >
        {TABS.map((tab) => {
          const selected = activeTab === tab;
          const count = counts?.[tab];
          return (
            <UnstyledButton
              key={tab}
              type="button"
              role="tab"
              aria-selected={selected}
              data-testid={`property-tab-${tab}`}
              onClickCapture={(e) => {
                if (strip.wasDragged()) { e.preventDefault(); e.stopPropagation(); }
              }}
              onClick={() => onTabChange(tab)}
              className="flex flex-none items-center gap-(--space-2) rounded-(--radius-md) px-(--space-3)"
              style={{
                height: 44,
                scrollSnapAlign: 'start',
                border: `1px solid ${selected ? 'var(--seed-base)' : 'var(--border-subtle)'}`,
                background: selected
                  ? 'color-mix(in srgb, var(--seed-base) 10%, var(--surface-primary))'
                  : 'var(--surface-primary)',
                color: selected ? 'var(--seed-base)' : 'var(--text-secondary)',
                fontSize: 'var(--text-body-sm)',
                fontWeight: selected
                  ? ('var(--font-semibold)' as unknown as number)
                  : ('var(--font-medium)' as unknown as number),
                whiteSpace: 'nowrap',
              }}
            >
              {t(TAB_LABEL_KEYS[tab])}
              {typeof count === 'number' && count > 0 && <CountPill count={count} />}
            </UnstyledButton>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Tab navigation for the property detail view: a tablist on desktop, a
 * selector on phones.
 *
 * @param props - The active tab, change handler and per-tab counts.
 * @returns The rendered navigation.
 */
export const TabBar: React.FC<TabBarProps> = (props) => {
  const isMobile = useIsMobile();
  return isMobile ? <MobileTabSelector {...props} /> : <DesktopTabs {...props} />;
};
